import React from 'react';
import { VendorBadge } from '../types';

interface VendorBadgeIconProps {
  badge: VendorBadge;
  earned: boolean;
  size?: 'sm' | 'md' | 'lg';
  showTooltip?: boolean;
}

/**
 * Reusable badge icon component (Issue #684, #955)
 * Displays badge icon with locked/unlocked state styling
 */
export default function VendorBadgeIcon({
  badge,
  earned,
  size = 'md',
  showTooltip = true,
}: VendorBadgeIconProps) {
  // Size configurations
  const sizeClasses = {
    sm: 'w-8 h-8 text-lg',
    md: 'w-12 h-12 text-2xl',
    lg: 'w-16 h-16 text-4xl',
  };

  // Determine if badge is verified type (special styling)
  const isVerified = badge.badge_type === 'verification';

  return (
    <div
      className={`
        ${sizeClasses[size]}
        flex items-center justify-center
        rounded-full
        border-2
        ${
          earned
            ? isVerified
              ? 'bg-teal-50 border-primary shadow-sm'
              : 'bg-white border-gray-300 shadow-sm'
            : 'bg-gray-100 border-gray-300 opacity-50 grayscale'
        }
        transition-all duration-200
        ${earned ? 'hover:scale-110' : ''}
        ${showTooltip ? 'cursor-help' : ''}
      `}
      title={
        showTooltip
          ? `${badge.name}${earned ? '' : ' (Locked)'}${badge.description ? ` - ${badge.description}` : ''}`
          : undefined
      }
      data-testid={`badge-icon-${badge.id}`}
    >
      {badge.icon ? (
        <span className="select-none">{badge.icon}</span>
      ) : (
        <span className="text-gray-400 text-sm font-bold">
          {badge.name.charAt(0).toUpperCase()}
        </span>
      )}
    </div>
  );
}
